'use client';

import { useEffect, useRef } from 'react';
import runAfterNextPaint from 'commons/runAfterNextPaint';
import useRudderStackAnalytics from './useRudderAnalytics';
let isAnalyticsHistoryInstrumented = false;
const RudderAnalyticsProvider = ({
  children
}) => {
  const analytics = useRudderStackAnalytics();
  const analyticsRef = useRef(null);
  // pathname + search only — hash-only history updates must not count as a new page
  // (see useLocationHash; Next may call pushState/replaceState for hash changes).
  const lastPushedRouteKeyRef = useRef('');
  const lastPushedPathnameRef = useRef(null);
  const lastRudderRouteKeyRef = useRef(null);
  const pushPageViewRef = useRef(() => {});
  analyticsRef.current = analytics;
  pushPageViewRef.current = () => {
    const routeKey = `${window.location.pathname}${window.location.search}`;
    const pageUrl = window.location.href;
    const routeChanged = lastPushedRouteKeyRef.current !== routeKey;
    if (routeChanged) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'Viewed a Page',
        pagePath: window.location.pathname || '/',
        pageUrl
      });
      lastPushedRouteKeyRef.current = routeKey;
      lastPushedPathnameRef.current = window.location.pathname;
    }
    const analyticsInstance = analyticsRef.current;
    if (analyticsInstance && lastRudderRouteKeyRef.current !== routeKey) {
      analyticsInstance.page('Auto track');
      lastRudderRouteKeyRef.current = routeKey;
    }
  };

  // Run before [analytics] so the first paint does not double-call analytics.page().
  useEffect(() => {
    if (!isAnalyticsHistoryInstrumented) {
      isAnalyticsHistoryInstrumented = true;
      const prevPush = window.history.pushState;
      const prevReplace = window.history.replaceState;
      window.history.pushState = function (...args) {
        const result = prevPush.apply(window.history, args);
        window.dispatchEvent(new Event('jb:location-change'));
        return result;
      };
      window.history.replaceState = function (...args_0) {
        const result_0 = prevReplace.apply(window.history, args_0);
        window.dispatchEvent(new Event('jb:location-change'));
        return result_0;
      };
    }

    // Search-only updates (e.g. PLP filters in useCategoryPageURL) call
    // replaceState inside the click, and the page-view tags fan out
    // synchronously through GTM and RudderStack. Run them after the paint;
    // the route key is read then, so back-to-back updates still dedupe.
    // A new pathname is a page change: Next pushes it from an insertion
    // effect, so its page view must go out straight away to reach the
    // dataLayer before the new page's mount events, e.g. productViewed
    // (CON-8270).
    const onLocationChange = () => {
      if (window.location.pathname !== lastPushedPathnameRef.current) {
        pushPageViewRef.current();
        return;
      }
      runAfterNextPaint(() => pushPageViewRef.current());
    };
    window.addEventListener('popstate', onLocationChange);
    window.addEventListener('jb:location-change', onLocationChange);
    pushPageViewRef.current();
    return () => {
      window.removeEventListener('popstate', onLocationChange);
      window.removeEventListener('jb:location-change', onLocationChange);
    };
  }, []);
  useEffect(() => {
    pushPageViewRef.current();
  }, [analytics]);
  return <>{children}</>;
};
export default RudderAnalyticsProvider;